import type {DocumentNode, FragmentDefinitionNode} from "graphql";

/**
 * Returns a copy of the given `DocumentNode` with fragment definitions sorted
 * alphabetically and placed after other definitions.
 *
 * This is done for compatibility with our GraphQL safelist.
 * The safelist is generated using `persistgraphql` library which outputs
 * fragments in alphabetical order. A mismatch in fragment order will result
 * in an error when `graphql-gateway` validates the operation against the
 * safelist.
 */
export function sortFragments(doc: DocumentNode): DocumentNode {
    // NOTE: We need to ensure that fragment names are sorted according to
    // English language rules. In order to exactly match how we generate
    // the GraphQL safelist in our tools, which are expected to be run on
    // a machine configured with an English language environment.
    // If the locale were left to be unspecified, then the comparison
    // behaviour on the client would be dictated by the user (operating
    // system) language preference. Potentially resulting in a different
    // fragments order at runtime, failing the GraphQL safelist check.
    const collator = new Intl.Collator("en");

    return {
        ...doc,
        // https://github.com/apollographql/persistgraphql/blob/master/src/common.ts#L30
        // https://github.com/Khan/persistgraphql/blob/master/src/common.ts#L31
        definitions: [...doc.definitions].sort((a, b) => {
            const aIsFragment = a.kind === "FragmentDefinition";
            const bIsFragment = b.kind === "FragmentDefinition";

            // If both aren't fragments, just leave them in place.
            if (!aIsFragment && !bIsFragment) {
                return 0;
            }

            // If both are fragments, sort them by their name.
            if (aIsFragment && bIsFragment) {
                const aName = (a as FragmentDefinitionNode).name.value;
                const bName = (b as FragmentDefinitionNode).name.value;
                return collator.compare(aName, bName);
            }

            // Move fragments to the end.
            return aIsFragment ? 1 : -1;
        }),
    };
}
